/**
 * Brand Registry - Dynamic Brand Page Configuration
 *
 * Centralized registry for all brand-specific pages.
 * Add new brands here - no need to modify App.tsx or BrandAwareRoot.
 *
 * Pattern:
 * - Brands with custom pages: import and register the component
 * - Brands without custom pages: use GenericBrandLanding fallback
 * - All brands get automatic routing based on domain detection
 */

import React from 'react';

// Brand-specific pages (lazy load for code splitting)
import PowerSpecLanding from '../pages/brands/PowerSpecLanding';
import PowerSpecDashboard from '../pages/brands/PowerSpecDashboard';
import VelocityMindLanding from '../pages/brands/VelocityMindLanding';
import VelocityMindDashboard from '../pages/brands/VelocityMindDashboard';
import AgencyFlowLanding from '../pages/brands/AgencyFlowLanding';
import AgencyFlowDashboard from '../pages/brands/AgencyFlowDashboard';
import ComplianceForgeLanding from '../pages/brands/ComplianceForgeLanding';
import ComplianceForgeDashboard from '../pages/brands/ComplianceForgeDashboard';
import EquilateralLanding from '../pages/brands/EquilateralLanding';
import EquilateralDashboard from '../pages/brands/EquilateralDashboard';

/**
 * Brand page configuration
 */
export interface BrandPageConfig {
  /** Landing page component (public, no auth) */
  landing: React.ComponentType | null;
  /** Dashboard component (protected, requires auth) */
  dashboard: React.ComponentType | null;
  /** Whether landing page is public (no auth required) */
  publicLanding: boolean;
  /** Route prefix for brand-specific routes */
  routePrefix: string;
}

/**
 * Brand Registry
 *
 * Add new brands here. Each brand can have:
 * - Custom landing page (or null for auth-required root)
 * - Custom dashboard (or null to use default)
 * - Public/private landing configuration
 */
export const BRAND_REGISTRY: Record<string, BrandPageConfig> = {
  // === ACTIVE BRANDS WITH CUSTOM PAGES ===

  powerspec: {
    landing: PowerSpecLanding,
    dashboard: PowerSpecDashboard,
    publicLanding: true,
    routePrefix: '/powerspec',
  },

  velocitymind: {
    landing: VelocityMindLanding,
    dashboard: VelocityMindDashboard,
    publicLanding: true,
    routePrefix: '/velocitymind',
  },

  agencyflow: {
    landing: AgencyFlowLanding,
    dashboard: AgencyFlowDashboard,
    publicLanding: true,
    routePrefix: '/agencyflow',
  },

  complianceforge: {
    landing: ComplianceForgeLanding,
    dashboard: ComplianceForgeDashboard,
    publicLanding: true,
    routePrefix: '/complianceforge',
  },

  equilateral: {
    landing: EquilateralLanding,
    dashboard: EquilateralDashboard,
    publicLanding: false, // Equilateral requires auth at root
    routePrefix: '/equilateral',
  },

  // === BRANDS PENDING CUSTOM PAGES (use generic fallback) ===

  modelmind: {
    landing: null, // Will use GenericBrandLanding
    dashboard: null,
    publicLanding: true,
    routePrefix: '/modelmind',
  },

  patternforge: {
    landing: null,
    dashboard: null,
    publicLanding: true,
    routePrefix: '/patternforge',
  },

  specmind: {
    landing: null,
    dashboard: null,
    publicLanding: true,
    routePrefix: '/specmind',
  },
};

/**
 * Get brand config with fallback for unknown brands
 */
export function getBrandConfig(brandCode: string): BrandPageConfig {
  return BRAND_REGISTRY[brandCode] || {
    landing: null,
    dashboard: null,
    publicLanding: false,
    routePrefix: `/${brandCode}`,
  };
}

/**
 * Check if a brand has a public landing page
 */
export function hasPublicLanding(brandCode: string): boolean {
  const config = getBrandConfig(brandCode);
  return config.publicLanding && config.landing !== null;
}

/**
 * Get all registered brand codes
 */
export function getRegisteredBrands(): string[] {
  return Object.keys(BRAND_REGISTRY);
}

/**
 * Get brands with public landing pages
 */
export function getPublicBrands(): string[] {
  return Object.entries(BRAND_REGISTRY)
    .filter(([_, config]) => config.publicLanding && config.landing !== null)
    .map(([code]) => code);
}
